import React, { Suspense, useCallback, useRef } from 'react';
import { useEffect } from 'react';
import './App.scss';
import { BrowserRouter, Switch, Route } from "react-router-dom";
import { Layout } from 'antd';
import AuthPage from "./components/AuthPage";
import MemberApi from "./api/MemberApi";
import Loader from "./components/AppLoader";
import Dashboard from "./components/dashboard/Dashboard";
import Storage from "./util/Storage";
import { isPublicPage, redirectToLogin, RoutesEnum } from './RoutesEnum';
import LightBusinessPage from "./components/order/LightBusinessPage";
import { env } from "./env/env";
import LightBusinessNotFound from './components/order/LightBusinessNotFound';
import useQueryString from "./hooks/useQueryString";
import _, { parseInt } from "lodash";
import { MemberStatusDto } from "./gen/client";
import { AxiosResponse } from "axios";
import { socketConnect } from "./services/SocketService";
import { useRecoilState, useSetRecoilState } from "recoil";
import { isOwnerState, locationIdState, memberState } from "./store/atoms";
import { getJSPath, getILJSPath, loadJS } from "./util/ScriptLoader";

const { Content } = Layout;

export default function App() {
  const [locationIdQuery] = useQueryString<string>('businessId', '');
  const [locationId, setLocationId] = useRecoilState(locationIdState);
  const setMember = useSetRecoilState(memberState);
  const setIsOwner = useSetRecoilState(isOwnerState);
  const [tkn] = useQueryString<string>('tkn', Storage.getToken());
  const throttledVisibilityChangeHandler = useRef(_.debounce(checkStillLoggedIn, 1000));

  useEffect(() => {
    loadJS(getJSPath(), () => {
      console.log('=> payment script loaded.');
    });
    loadJS(getILJSPath(), () => {
      console.log('=> IL script loaded')
    })
  }, []);

  useEffect(() => {
    if (!isPublicPage(window.location.href)) {
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          throttledVisibilityChangeHandler.current();
        }
      }, false);

      window.addEventListener('storage', () => {
        if (!Storage.getToken()) {
          redirectToLogin();
        }
      });
    }
  }, []);

  useEffect(() => {
    if (locationIdQuery) {
      setLocationId(locationIdQuery)
    }
  }, [locationIdQuery, setLocationId])

  const logoutIfRequired = useCallback((resp: AxiosResponse<MemberStatusDto>) => {
    if (!resp.data.authenticated) {
      redirectToLogin();
    }
  }, []);

  useEffect(() => {
    if (!locationId) return;
    MemberApi.getStatus(tkn, parseInt(locationId)).then(resp => {
      logoutIfRequired(resp);
      const { member } = resp.data;

      socketConnect(locationId, member.name, member.id);

      Storage.setMemberId(member.id);
      setMember(member);
      setIsOwner(resp.data.owner);
    });
  }, [tkn, logoutIfRequired, locationId, setMember, setIsOwner]);

  function checkStillLoggedIn() {
    MemberApi.getStatus(null, parseInt(locationId)).then(logoutIfRequired);
  }

  useEffect(() => {
    if (!!tkn && tkn !== 'null') {
      Storage.setToken(tkn);
    }
  }, [tkn]);

  useEffect(() => {
    if (!!locationId && locationId !== 'null') {
      Storage.setBusinessId(locationId);
    }
  }, [locationId]);

  return (
    <BrowserRouter>
      <Suspense fallback={<Loader />}>
        <Layout className={`app`}>
          <Content>
            <Switch>
              <Route path={`${RoutesEnum.Order}/:url/menus/:menu`}><LightBusinessPage /></Route>
              <Route path={`${RoutesEnum.Order}/:url`}><LightBusinessPage /></Route>
              <Route path={RoutesEnum.App}><Dashboard /></Route>
              {env.debug && <Route path={RoutesEnum.Root}><AuthPage /></Route>}
              <Route path={`${RoutesEnum.LightOrder}/:url`}><LightBusinessPage /></Route>
              <Route path="*"><LightBusinessNotFound found={false} /></Route>
            </Switch>
          </Content>
        </Layout>
      </Suspense>
    </BrowserRouter>
  );
}
